// File: nodes/EnhancedConditionNode.jsx
import React from 'react';
import { Handle, Position } from 'reactflow';
import {
    Box,
    Flex,
    Text,
    Tag,
    IconButton,
    HStack
} from '@chakra-ui/react';
import {
    FaChartBar,
    FaUser,
    FaCodeBranch
} from 'react-icons/fa';
import { FiCopy, FiEdit, FiTrash2 } from 'react-icons/fi';

const EnhancedConditionNode = ({ data }) => {
    const getConditionTypeInfo = () => {
        const type = data.conditionType || 'event';
        switch (type) {
            case 'event':
                return { icon: <FaChartBar size={16} />, label: 'Event Check' };
            case 'conversion':
                return { icon: <FaChartBar size={16} />, label: 'Conversion Status' };
            case 'journey':
                return { icon: <FaCodeBranch size={16} />, label: 'Journey State' };
            case 'profile':
                return { icon: <FaUser size={16} />, label: 'Profile/Attribute' };
            case 'count':
                return { icon: <FaChartBar size={16} />, label: 'Event Count' };
            case 'sequence':
                return { icon: <FaCodeBranch size={16} />, label: 'Event Sequence' };
            default:
                return { icon: <FaCodeBranch size={16} />, label: 'Condition' };
        }
    };

    const getConditionSummary = () => {
        const type = data.conditionType || 'event';

        if (type === 'event') {
            return data.eventToEvaluate || 'Not configured';
        } else if (type === 'conversion') {
            const conversionDef = data.conversionDefinition || 'Not set';
            const matchEntity = data.matchEntityBy || 'order_id';
            return `${conversionDef} (${matchEntity})`;
        } else if (type === 'journey') {
            return data.journeyState || 'Not configured';
        } else if (type === 'profile') {
            return `${data.attributeSource || 'Profile'}.${data.fieldSelector || 'field'
                }`;
        } else if (type === 'count') {
            return `${data.eventName || 'Event'} count ${data.countOperator || '>'
                } ${data.countValue || '0'}`;
        } else if (type === 'sequence') {
            return 'Multi-step sequence';
        }

        return 'Not configured';
    };

    const typeInfo = getConditionTypeInfo();

    return (
        <Box
            bg="white"
            border="2px solid"
            borderColor="#FBAF3F"
            borderRadius="xl"
            boxShadow={data.selected ? 'xl' : 'lg'}
            minW="280px"
            position="relative"
        >
            <Handle
                type="target"
                position={Position.Left}
                style={{ background: 'black' }}
            />

            {/* Header */}
            <Flex
                bgGradient="linear(to-r, #FBAF3F, #F59E0B)"
                color="white"
                px={4}
                py={2.5}
                borderTopRadius="xl"
                align="center"
                gap={2}
            >
                {typeInfo.icon}
                <Text noOfLines={1} flex={1} fontSize="sm" fontWeight="medium">
                    {typeInfo.label}
                </Text>
                <Tag
                    fontSize="10px"
                    px={1.5}
                    py={0}
                    h={4}
                    bg="whiteAlpha.200"
                    color="white"
                    borderRadius="sm"
                    border="1px solid"
                    borderColor="whiteAlpha.400"
                    display="flex"
                    alignItems="center"
                >
                    Enhanced
                </Tag>
                <HStack spacing={1}>
                    <IconButton
                        icon={<FiEdit size={14} />}
                        size="xs"
                        variant="ghost"
                        color="white"
                        _hover={{ bg: 'whiteAlpha.300' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            data.onEdit?.(data.id);
                        }}
                        aria-label="Edit"
                    />
                    <IconButton
                        icon={<FiCopy size={14} />}
                        size="xs"
                        variant="ghost"
                        color="white"
                        _hover={{ bg: 'whiteAlpha.300' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            data.onDuplicate?.(data.id);
                        }}
                        aria-label="Duplicate"
                    />
                    <IconButton
                        icon={<FiTrash2 size={14} />}
                        size="xs"
                        variant="ghost"
                        color="white"
                        _hover={{ bg: 'whiteAlpha.300' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            data.onDelete?.(data.id);
                        }}
                        aria-label="Delete"
                    />
                </HStack>
            </Flex>

            {/* Body */}
            <Box p={3.5}>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                    {data.label || 'Enhanced Condition'}
                </Text>

                {/* Condition Summary */}
                <Box
                    fontSize="xs"
                    bg="orange.50"
                    border="1px solid"
                    borderColor="orange.200"
                    borderRadius="md"
                    px={2}
                    py={1.5}
                    mb={3}
                    wordBreak="break-word"
                >
                    {getConditionSummary()}
                </Box>

                {/* Branch Paths */}
                <Flex gap={2} mt={3} position="relative">
                    <Box flex={1} textAlign="center" borderRight="1px solid" borderColor="gray.200">
                        <Text fontSize="xs" fontWeight="medium" color="green.700">
                            ✓ {data.yesLabel || 'Yes'}
                        </Text>
                    </Box>

                    <Box flex={1} textAlign="center">
                        <Text fontSize="xs" fontWeight="medium" color="red.700">
                            ✗ {data.noLabel || 'No'}
                        </Text>
                    </Box>

                    {/* YES Handle */}
                    <Handle
                        type="source"
                        position={Position.Right}
                        id="yes"
                        className="branch-handle"
                        style={{
                            background: '#48BB78',
                            top: '-50px',
                            transform: 'translateY(-50%)',
                            // See flowHandles.css .branch-handle: this row's wrapper Flex
                            // has no border/padding of its own, so the offset is just the
                            // body padding (p={3.5}=14px) + node border (2px) + half the
                            // rendered 8px dot (6px forced width + the default 1px ring).
                            '--flow-branch-offset': '-20px',
                        }}
                    />

                    {/* NO Handle */}
                    <Handle
                        type="source"
                        position={Position.Right}
                        id="no"
                        className="branch-handle"
                        style={{
                            background: '#EF4444',
                            top: '-20px',
                            transform: 'translateY(-50%)',
                            '--flow-branch-offset': '-20px',
                        }}
                    />
                </Flex>

            </Box>
        </Box>
    );
};

export default EnhancedConditionNode;